import * as React from 'react';
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery, Dialog, AppBar, Toolbar, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { String_EmailReceiptDialog_Title, String_EmailReceiptDialog_Instruction, 
    String_System_ErrorInvalidEmail, String_System_Done } from '../../utilities/StringHelper';
import TextField, { TextFieldProps, TextFieldLabelPosition, TextFieldChangeHandler } from '../common/TextField';
import SmallButton, { SmallButtonProps } from '../common/Buttons/SmallButton';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { Skin } from '../../models/common/Skin';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';


const useStyles = makeStyles({
    appBar: {
        position: 'relative',
        backgroundColor: props => (props as Skin).primary_main_color + ' !important',
    },
    buttonContainer: {
        padding: ElementDimensions.padding10 + 'px',
    },
    closeButton: {
        height: '30px',
        width: '30px',
        color: themePrimary.palette.primary.contrastText,
    },
    errorMessageContainer: {
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center',
        height: '2em'
    },
    errorMessage: {
        fontSize: '1rem',
        color: themePrimary.palette.error.main,
        fontFamily: 'LeagueSpartan-Regular !important',
    },
    instructionContainer: {

    },
    instructionText: {
        padding: ElementDimensions.padding5 + 'px',
        fontFamily: 'LeagueSpartan-Regular !important',
    },
    root: {

    },
    rootLandscape: {
        left: '25vw !important',
        width: '50vw',
    },
    textFieldOuterContainer: {
        display: 'flex',
    },
    textFieldInnerContainer: {
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    title: {
        marginLeft: ElementDimensions.margin10 + 'px',
        flex: 1,
        color: themePrimary.palette.primary.contrastText,
    },
});

export type EmailReceiptDialogCloseClickHandler = (event: React.MouseEvent<HTMLElement>) => any;
export type EmailReceiptDialogDoneClickHandler = (event: React.MouseEvent<HTMLElement>, emailAddress: string) => any;

export interface EmailReceiptDialogProps {
    open: boolean;
    onDoneHandler: EmailReceiptDialogDoneClickHandler;
    onCloseHandler: EmailReceiptDialogCloseClickHandler;
}

const EmailReceiptDialog = (props: EmailReceiptDialogProps) => {
    const { open, onDoneHandler, onCloseHandler } = props;
    const isPortrait = true;
    const [isError, setIsError] = React.useState<boolean>(false);
    const [emailAddress, setEmailAddress] = React.useState<string>('');
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
        setIsError(false);
        setEmailAddress('');
        onCloseHandler(event);
    };

    const handleEmailAddressChange: TextFieldChangeHandler = (newValue: string) => {
        setEmailAddress(newValue.trim());
        setIsError(false);
    };

    const emailProps: TextFieldProps = {
        type: null,
        label: '',
        text: emailAddress,
        width: 300,
        disabled: false,
        labelPosition: TextFieldLabelPosition.Left,
        onChangeHandler: handleEmailAddressChange,
    }

    const handleOkButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(emailAddress.trim())) {
            onDoneHandler(event, emailAddress.trim());
            setEmailAddress('');
        }
        else {
            setIsError(true);            
        }
    }

    const buttonProps: SmallButtonProps = {
        alternateColor: false,
        disabled: false,
        label: String_System_Done.find(s => s.key === userState.state.locale)!!.value,
        onClickHandler: handleOkButtonClick,
    }

    return (
        <Dialog open={open} onClose={handleClose} disableBackdropClick={true} className={isPortrait ? classes.root : classes.rootLandscape}>
            <AppBar className={classes.appBar}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                        <CloseIcon className={classes.closeButton} />
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        {String_EmailReceiptDialog_Title.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>
                </Toolbar>
            </AppBar>   
            <div className={classes.instructionContainer}>
                <Typography variant='h6' className={classes.instructionText}>
                    {String_EmailReceiptDialog_Instruction.find(s => s.key === userState.state.locale)!!.value}
                </Typography>
            </div>
            <div className={classes.textFieldOuterContainer}>
                <div className={classes.textFieldInnerContainer}>
                    <TextField 
                        type={emailProps.type}
                        label={emailProps.label}
                        text={emailProps.text}
                        width={emailProps.width}
                        disabled={emailProps.disabled}
                        labelPosition={emailProps.labelPosition}
                        onChangeHandler={emailProps.onChangeHandler} />
                </div>
            </div>
            <div className={classes.errorMessageContainer}>
                {isError &&
                    <Typography variant='body1' className={classes.errorMessage}>
                        {String_System_ErrorInvalidEmail.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>
                }
            </div>
            <div className={classes.buttonContainer}>
                <SmallButton 
                    alternateColor={buttonProps.alternateColor}
                    disabled={buttonProps.disabled}
                    label={buttonProps.label}
                    onClickHandler={buttonProps.onClickHandler}/>
            </div>
        </Dialog>
    );
};

export default EmailReceiptDialog;